import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { createReceipt, createReceiptFromImage, getConfig } from '../services/api';

const EXAMPLE = `KIWI GRUNERLØKKA
Thorvald Meyers gate 56
Dato: 24.03.2026 14:32

Olje oliven ex vir 750ml        49,90
Lettmelk 1l                     16,90
Kyllingfilet 500g               44,95
Pasta fusilli 500g               9,90
Tomater løse 0,456kg x 29,90   13,64
Norvegia skive 400g             52,90

Sum:                           188,19
Betalt Vipps:                  188,19`;

const ACCEPTED_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'image/heic', 'image/heif'];

export default function UploadPage() {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);

  const [mode, setMode] = useState('text'); // 'text' | 'image'
  const [text, setText] = useState('');
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [dragging, setDragging] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [aiProviderName, setAiProviderName] = useState('AI');

  useEffect(() => {
    getConfig()
      .then((cfg) => setAiProviderName(cfg.aiProviderName))
      .catch(() => {}); // non-critical — falls back to the generic label
  }, []);

  const handleFileSelect = (file) => {
    if (!file) return;
    if (!ACCEPTED_TYPES.includes(file.type) && !file.name.match(/\.(heic|heif)$/i)) {
      setError('Please select a JPEG, PNG, WebP, or HEIC image.');
      return;
    }
    setImageFile(file);
    setError('');
    // Preview (not available for HEIC but that's OK)
    const reader = new FileReader();
    reader.onload = (e) => setImagePreview(e.target.result);
    reader.readAsDataURL(file);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    handleFileSelect(e.dataTransfer.files[0]);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      let receipt;
      if (mode === 'image') {
        receipt = await createReceiptFromImage(imageFile);
      } else {
        receipt = await createReceipt(text);
      }
      navigate(`/receipts/${receipt.id}`);
    } catch (err) {
      setError(err.response?.data?.error || `Failed to process receipt. Check your ${aiProviderName} API key.`);
    } finally {
      setLoading(false);
    }
  };

  const canSubmit = mode === 'text' ? text.trim().length > 0 : imageFile !== null;

  return (
    <div style={{ maxWidth: 700 }}>
      <h2 style={{ marginBottom: '0.25rem' }}>Upload receipt</h2>
      <p className="muted" style={{ marginBottom: '1.5rem' }}>
        Paste receipt text or upload a photo — {aiProviderName} will extract items and update price history.
      </p>

      {/* Mode toggle */}
      <div style={{ display: 'flex', gap: 8, marginBottom: '1.25rem' }}>
        {['text', 'image'].map((m) => (
          <button
            key={m}
            onClick={() => { setMode(m); setError(''); }}
            className={mode === m ? 'btn-primary' : 'btn-secondary'}
            style={{ textTransform: 'capitalize' }}
          >
            {m === 'text' ? 'Paste text' : 'Upload photo'}
          </button>
        ))}
      </div>

      <form onSubmit={handleSubmit}>
        {mode === 'text' ? (
          <div className="card" style={{ marginBottom: '1rem' }}>
            <textarea
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder="Paste receipt text here…"
              rows={16}
              style={{
                width: '100%',
                fontFamily: 'monospace',
                fontSize: 13,
                resize: 'vertical',
                border: 'none',
                outline: 'none',
                padding: 0,
              }}
            />
          </div>
        ) : (
          <div
            onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
            onDragLeave={() => setDragging(false)}
            onDrop={handleDrop}
            onClick={() => fileInputRef.current?.click()}
            style={{
              border: `2px dashed ${dragging ? 'var(--color-primary)' : 'var(--color-border)'}`,
              borderRadius: 'var(--radius)',
              background: dragging ? 'var(--color-primary-bg)' : 'var(--color-surface)',
              padding: '2.5rem',
              textAlign: 'center',
              cursor: 'pointer',
              marginBottom: '1rem',
              transition: 'all 0.15s',
            }}
          >
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*,.heic,.heif"
              style={{ display: 'none' }}
              onChange={(e) => handleFileSelect(e.target.files[0])}
            />
            {imagePreview ? (
              <div>
                <img
                  src={imagePreview}
                  alt="Receipt preview"
                  style={{ maxHeight: 300, maxWidth: '100%', borderRadius: 4, marginBottom: 12 }}
                />
                <div style={{ fontSize: 13, color: 'var(--color-text-muted)' }}>
                  {imageFile?.name} — click to change
                </div>
              </div>
            ) : (
              <div>
                <div style={{ fontSize: 36, marginBottom: 8 }}>📷</div>
                <div style={{ fontWeight: 500, marginBottom: 4 }}>
                  Drop receipt photo here or click to browse
                </div>
                <div className="muted" style={{ fontSize: 13 }}>
                  JPEG, PNG, WebP, or HEIC — iPhone camera roll works great
                </div>
              </div>
            )}
          </div>
        )}

        {error && <p className="error" style={{ marginBottom: '1rem' }}>{error}</p>}

        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <button
            type="submit"
            className="btn-primary"
            disabled={loading || !canSubmit}
            style={{ padding: '0.6rem 1.5rem' }}
          >
            {loading ? 'Processing with AI…' : 'Parse & save receipt'}
          </button>
          {mode === 'text' && (
            <button
              type="button"
              className="btn-secondary"
              onClick={() => setText(EXAMPLE)}
            >
              Load example
            </button>
          )}
        </div>

        {loading && (
          <p className="muted" style={{ marginTop: '0.75rem', fontSize: 13 }}>
            Sending to {aiProviderName} for parsing — this usually takes 5–20 seconds…
          </p>
        )}
      </form>
    </div>
  );
}
